import { ThemeWrapper } from "@verity/components";
import { CustomSettingData } from "../../settings-page";
import { LmsAccount, ScopeSettingsInfo } from "../../../../types/general-types";
import {
	AlphabetImages,
	CoreButton,
	CoreCheckbox,
	CoreModal,
	CoreRadioGroup,
	CoreSearchField,
	CoreSearchFieldPaper,
	CoreTable,
	CoreTableColumn,
	CoreTextBox,
	coreToast,
	CoreTreeView,
	RadioOption,
} from "@verity/components/core";
import {
	addNodeToTree,
	convertListToTree,
	findNodeById,
	findNodeByParentId,
	fixHierarchy,
	getAllNodeIds,
	removeNodeById,
} from "./TreeHelperFunctions";
import { useContext, useEffect, useId, useState } from "react";
import NoAccountListSvg from "../../../../images/portal/no-account-list-icon.svg";
import NoMangeAccountSvg from "../../../../images/portal/no-manage-account-icon.svg";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import styles from "./index.module.css";
import apiClient from "../../../../helpers/api-client";
import i18n from "../../../../i18n";
import { Link, Skeleton, Typography } from "@mui/material";
import { Lms } from "@verity/api-client/types";
import { PortalControllerContext } from "../../../context";
import { initiateAuthenticationRedirect } from "../../../../helpers/oauth";
import { courseUrlToCourseId, determineCourseInputType, validateCourseId } from "../../../../helpers/scope";

enum ScopeSettingsList {
	ALL_COURSES = "allCourses",
	SELECTED_COURSES = "selectedCourses",
	SELECTED_SUBACCOUNTS = "selectedAccounts",
}

export interface CustomLabelProps {
	children: string;
	className: string;
	id: string;
}

export const CustomLabel = (props: CustomLabelProps) => {
	const imageName = props.children
		.replace(/[^a-z]/gi, "")
		.charAt(0)
		.toUpperCase();
	return (
		<div
			style={{
				display: "flex",
				fontSize: "16px",
				alignItems: "center",
				flexBasis: "100%",
			}}
		>
			<img
				src={AlphabetImages[imageName] ?? AlphabetImages["default"]}
				style={{ marginRight: "8px", width: "24px", height: "24px" }}
				alt={props.children[0]}
			/>
			{props.children}
		</div>
	);
};

export const InstituteScopeSettings = (props: {
	loadData: { scopeSettingsData: ScopeSettingsInfo };
	setting: CustomSettingData<{ scopeSettingsData: ScopeSettingsInfo }>;
	onSettingChange: (value: { scopeSettingsData: ScopeSettingsInfo }) => void;
	disabled?: boolean;
	lmsType: Lms;
	ariaLabelledByTitleId?: string;
}) => {
	const { portalContext, action, setAction } = useContext(PortalControllerContext);
	const controlId = useId();

	const [scopeSettingsLoadedData, setScopeSettingsLoadedData] = useState(
		props.setting.value?.scopeSettingsData ?? {
			...props.loadData.scopeSettingsData,
			scopeMode:
				action === "syncLmsAccounts"
					? ScopeSettingsList.SELECTED_SUBACCOUNTS
					: props.loadData.scopeSettingsData.scopeMode,
		},
	);

	const [showManageAccountsModal, setShowManageAccountsModal] = useState(action === "syncLmsAccounts");
	const [searchBarInput, setSearchBarInput] = useState("");
	const [isModalLoading, setIsModalLoading] = useState(false);
	const [filteredUnselectedTree, setFilteredUnselectedTree] = useState<LmsAccount[]>([]);
	const [filteredSelectedTree, setFilteredSelectedTree] = useState<LmsAccount[]>([]);

	const [unSelectedTree, setUnSelectedTree] = useState<LmsAccount[]>([]);
	const [selectedTree, setSelectedTree] = useState<LmsAccount[]>([]);
	const [selectedCourseList, setSelectedCourseList] = useState(
		props.setting.value?.scopeSettingsData.selectedCourses ?? props.loadData.scopeSettingsData.selectedCourses,
	);
	const [newCourseIdCounter, setNewCourseIdCounter] = useState(-1);

	const [courseToAdd, setCourseToAdd] = useState("");
	const [coursesToDelete, setCoursesToDelete] = useState<Array<string>>([]);

	const [isRemoveCourseModalOpen, setIsRemoveCourseModalOpen] = useState<boolean>(false);

	useEffect(() => {
		if (action === "syncLmsAccounts" && setAction) {
			onSyncAccountClick()
				.then(() => setAction(undefined))
				.catch((e) => {
					setAction(undefined);
					console.error(e);
				});
		}
	}, []);

	useEffect(() => {
		if (searchBarInput.trim() === "") {
			setFilteredUnselectedTree(unSelectedTree);
			setFilteredSelectedTree(selectedTree);
		} else {
			setFilteredUnselectedTree(filterSubAccountTreeBySearchText(unSelectedTree, searchBarInput));
			setFilteredSelectedTree(filterSubAccountTreeBySearchText(selectedTree, searchBarInput));
		}
	}, [searchBarInput, unSelectedTree, selectedTree]);

	const onManageCanvasAccountClick = () => {
		setSelectedTree(convertListToTree(scopeSettingsLoadedData.allAccounts.filter((account) => account.verityEnabled)));
		const verityDisabledAccounts: LmsAccount[] = scopeSettingsLoadedData.allAccounts.filter(
			(account) => !account.verityEnabled,
		);
		setUnSelectedTree(convertListToTree(verityDisabledAccounts));
		setShowManageAccountsModal(true);
	};

	const onSaveAccountClick = () => {
		// const subAccountsCandidates = updateVerityEnabled(selectedTree, true);
		const verityEnabledNodeIds = getAllNodeIds(selectedTree);
		setScopeSettingsLoadedData({
			...scopeSettingsLoadedData,
			scopeMode: ScopeSettingsList.SELECTED_SUBACCOUNTS,
			allAccounts: scopeSettingsLoadedData.allAccounts.map((account) => {
				if (verityEnabledNodeIds.find((id) => id === account.id)) {
					account.verityEnabled = true;
				} else {
					account.verityEnabled = false;
				}
				return account;
			}),
		});
		setShowManageAccountsModal(false);
	};

	const onSyncAccountClick = async () => {
		try {
			const oauthTokenExists = await apiClient.oauth.refreshStoredToken();
			if (props.lmsType !== "BlackboardLearn" && props.lmsType !== "moodle" && !oauthTokenExists) {
				const instituteInfo = await apiClient.lmsInfo.getInstituteLmsInfo();
				if (!instituteInfo?.origin) {
					throw Error(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.lms"));
				} else if (!instituteInfo?.lmsApiClientId || instituteInfo.lmsApiClientId.trim() === "") {
					throw Error(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.noFeature"));
				} else if (!portalContext.courseId) {
					throw Error("No course found.");
				}
				localStorage.setItem(
					"initialPortalState",
					JSON.stringify({
						action: "syncLmsAccounts",
					}),
				);
				await initiateAuthenticationRedirect({
					courseId: portalContext.courseId,
					callbackUrl: window.location.href,
				});
			} else {
				setIsModalLoading(true);
				await apiClient.institute.syncLmsAccounts();
				coreToast.success(
					<Typography>
						{i18n.t("subAccount.syncMessage", {
							plural: i18n.t(`subAccount.plural.${props.lmsType}`),
							singular: i18n.t(`subAccount.singular.${props.lmsType}`),
						})}
					</Typography>,
				);
			}
		} catch (err) {
			coreToast.error(i18n.t("subAccount.syncFailMsg"));
			console.error(err);
		} finally {
			setIsModalLoading(false);
		}
	};

	const onAddClick = (idStr: string) => {
		const id = Number(idStr);
		const foundNode = findNodeById(unSelectedTree, id);
		if (foundNode) {
			const newUnselectedTree = removeNodeById(unSelectedTree, foundNode);
			setUnSelectedTree(newUnselectedTree);
			const newSelectedTree = addNodeToTree(selectedTree, foundNode);
			setSelectedTree(fixHierarchy(newSelectedTree));
		}
	};

	const onRemoveClick = (idStr: string) => {
		const id = Number(idStr);
		const foundNode = findNodeById(selectedTree, id);
		if (foundNode) {
			const newSelectedTree = removeNodeById(selectedTree, foundNode);
			setSelectedTree(newSelectedTree);
			const newUnSelectedTree = addNodeToTree(unSelectedTree, foundNode);
			setUnSelectedTree(...[fixHierarchy(newUnSelectedTree)]);
		}
	};

	const CustomAddLabel = (props: CustomLabelProps) => {
		const imageName = props.children
			.replace(/[^a-z]/gi, "")
			.charAt(0)
			.toUpperCase();

		return (
			<div
				style={{
					display: "flex",
					fontSize: "16px",
					alignItems: "center",
					flexBasis: "100%",
					justifyContent: "space-between",
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
					}}
				>
					<img
						src={AlphabetImages[imageName] ?? AlphabetImages["default"]}
						style={{ marginRight: "8px", width: "24px", height: "24px" }}
						alt={props.children[0]}
					/>
					{props.children}
				</div>
				<div
					style={{
						display: "flex",
						flexBasis: "10%",
						justifyContent: "right",
					}}
					onClick={() => onAddClick(props.id)}
				>
					<AddIcon />
				</div>
			</div>
		);
	};

	const CustomRemoveLabel = (props: CustomLabelProps) => {
		const imageName = props.children
			.replace(/[^a-z]/gi, "")
			.charAt(0)
			.toUpperCase();
		const currentNode = findNodeById(selectedTree, Number(props.id));
		const parentNode = findNodeByParentId(selectedTree, currentNode?.parentLmsAccountId ?? "");
		return (
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					fontSize: "16px",
					alignItems: "center",
					flexBasis: "100%",
					justifyContent: "space-between",
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
					}}
				>
					<img
						src={AlphabetImages[imageName] ?? AlphabetImages["default"]}
						style={{ marginRight: "8px", width: "24px", height: "24px" }}
						alt={props.children[0]}
					/>
					{props.children}
				</div>
				{parentNode === null && (
					<div
						style={{
							display: "flex",
							flexBasis: "10%",
							justifyContent: "right",
						}}
						onClick={() => onRemoveClick(props.id)}
					>
						<RemoveIcon />
					</div>
				)}
			</div>
		);
	};

	/* Handles processing of the add course input */
	const onAddCourseClick = () => {
		try {
			/* Warn if trying to add an empty prompt */
			if (courseToAdd.trim().length === 0) {
				coreToast.error(i18n.t("portal.components.settingsPageTSX.emptyCourse"));
				return;
			}

			const containsUrlComponents = determineCourseInputType(courseToAdd) === "url";

			/* Attempt to generate a valid (HTTPS) URL if the request contains url components */
			let url: URL | null = null;
			if (containsUrlComponents) {
				try {
					url = new URL(courseToAdd);

					if (url.protocol !== "https:") {
						coreToast.error(i18n.t("portal.components.settingsPageTSX.invalidProtocol"));
						return;
					}
				} catch (e) {
					coreToast.error(i18n.t("portal.components.settingsPageTSX.invalidUrl"));
					return;
				}
			}

			/* Extract the course ID */
			const courseId = url ? courseUrlToCourseId(props.lmsType as Lms, url) : courseToAdd;
			if (!courseId) {
				coreToast.error(i18n.t("portal.components.settingsPageTSX.invalidCourse"));
				return;
			}

			/* Validate the course ID */
			const isValidCourseId = validateCourseId(props.lmsType as Lms, courseId);
			if (!isValidCourseId) {
				coreToast.error(i18n.t("portal.components.settingsPageTSX.invalidCourse"));
				return;
			}

			/* Detect duplicates */
			if (selectedCourseList.find((course) => course.courseId === courseId)) {
				coreToast.error(i18n.t("portal.components.settingsPageTSX.duplicateCourse"));
				return;
			}

			/* Define & Add the new course */
			const newCourse: {
				id: number;
				courseId: string;
				courseName: string;
				courseUrl: string;
			} = {
				id: newCourseIdCounter,
				courseId: courseId,
				courseName: "",
				courseUrl: url?.toString() ?? "",
			};

			/* Set & Reset for future calls */
			setNewCourseIdCounter(newCourseIdCounter - 1);
			setCourseToAdd("");
			setSelectedCourseList([newCourse, ...selectedCourseList]);
			setScopeSettingsLoadedData({
				...scopeSettingsLoadedData,
				selectedCourses: [newCourse, ...selectedCourseList],
			});

			/* Successfully added, display toast */
			coreToast.success(i18n.t("portal.components.settingsPageTSX.addCourse"));
		} catch (e) {
			coreToast.error(i18n.t("portal.components.settingsPageTSX.defaultError"));
		}
	};

	const courseColumns: CoreTableColumn<(typeof scopeSettingsLoadedData.selectedCourses)[0]>[] = [
		{
			header: (
				<CoreCheckbox
					checked={coursesToDelete.length > 0 && selectedCourseList.length === coursesToDelete.length}
					indeterminate={coursesToDelete.length > 0 && coursesToDelete.length < selectedCourseList.length}
					inputProps={{ "aria-label": i18n.t("portal.components.settingsPageTSX.selectAllCourses") }}
					onClick={() => {
						if (selectedCourseList.length === coursesToDelete.length) {
							setCoursesToDelete([]);
						} else {
							const newCoursesToDelete = selectedCourseList.map((course) => course.courseId);
							setCoursesToDelete(newCoursesToDelete);
						}
					}}
					sx={{
						"& .MuiSvgIcon-root": {
							borderColor: "grey",
						},
						"&.MuiCheckbox-indeterminate .MuiSvgIcon-root": {
							color: "#0B6F85",
						},
						"&.Mui-checked .MuiSvgIcon-root": {
							color: "#0B6F85",
						},
					}}
				/>
			),
			key: "remove",
			width: "10%",
			cell: (row) => (
				<div
					style={{
						display: "flex",
						flexBasis: "10%",
						justifyContent: "left",
						cursor: "pointer",
					}}
				>
					<CoreCheckbox
						checked={coursesToDelete.includes(row.data.courseId)}
						inputProps={{ "aria-label": i18n.t("portal.components.settingsPageTSX.selectCourse") }}
						onClick={() => {
							if (coursesToDelete.includes(row.data.courseId)) {
								// already selected
								const newCoursesToDelete = coursesToDelete.filter((courseId) => courseId !== row.data.courseId);
								setCoursesToDelete(newCoursesToDelete);
							} else {
								// not selected yet
								setCoursesToDelete([...coursesToDelete, row.data.courseId]);
							}
						}}
						sx={{
							"& .MuiSvgIcon-root": {
								borderColor: "grey",
							},
							"&.Mui-checked": {
								color: "#0B6F85",
							},
						}}
					/>
				</div>
			),
		},
		{
			header: i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.courseID"),
			key: "cId",
			truncateString: true,
			showTooltip: true,
			cell: (row) => row.data.courseId,
			descendingComparator: (a, b) => (b.courseId ?? "").toLowerCase().localeCompare((a?.courseId ?? "").toLowerCase()),
			width: "15%",
			align: "left",
		},
		{
			header: i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.courseURL"),
			key: "cUrl",
			width: "75%",
			truncateString: true,
			showTooltip: true,
			cell: (row) => (
				<Link
					href={row.data.courseUrl}
					target="_blank"
					rel="noreferrer"
					sx={{ fontSize: "16px", padding: "2px", "&:focus": { outlineOffset: "-2px" } }}
				>
					{row.data.courseUrl}
				</Link>
			),
			align: "left",
		},
	];

	useEffect(() => {
		props.onSettingChange({ scopeSettingsData: scopeSettingsLoadedData });
	}, [props, scopeSettingsLoadedData]);

	const radioOptions: RadioOption[] = Object.values(ScopeSettingsList).map((scope) => ({
		value: scope,
		label: i18n.t(`portal.components.settings.instituteMethodScopeSettings.radioGroup.${scope}`, {
			lms: i18n.t(`subAccount.plural.${props.lmsType}`),
		}),
		disabled: props.disabled,
	}));

	return (
		<ThemeWrapper>
			<div
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					gap: "28px",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
					<CoreRadioGroup
						radioOptions={radioOptions}
						onChange={(e) =>
							setScopeSettingsLoadedData({
								...scopeSettingsLoadedData,
								scopeMode: e.target.value as ScopeSettingsList,
							})
						}
						selectedValue={scopeSettingsLoadedData.scopeMode}
						radioLabelFontSize={15}
						radioGroupAriaLabelledby={props.ariaLabelledByTitleId}
					/>
				</div>
			</div>

			{scopeSettingsLoadedData.scopeMode === ScopeSettingsList.SELECTED_SUBACCOUNTS && (
				<>
					<CoreButton
						onClick={() => onManageCanvasAccountClick()}
						style={{
							height: "35px",
							fontSize: "15px",
							marginTop: "15px",
						}}
					>
						{i18n.t("subAccount.manage", {
							type: i18n.t(`subAccount.plural.${props.lmsType}`),
						})}
					</CoreButton>

					<div
						style={{
							display: "flex",
							marginTop: "25px",
							border: "2px solid #e0e0e0",
							padding: "15px",
							height: "350px",
							borderRadius: "5px",
							overflow: "auto",
						}}
					>
						<CoreTreeView
							disableSelection={true}
							items={convertListToTree(scopeSettingsLoadedData.allAccounts.filter((account) => account.verityEnabled))}
							label={CustomLabel}
							getItemId={(item: LmsAccount) => `${item.id}`}
							getItemLabel={(item: LmsAccount) => `${item?.accountName} (${item?.lmsAccountId})`}
							noResultString={i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.noAccFound")}
						/>
					</div>
				</>
			)}

			{scopeSettingsLoadedData.scopeMode === ScopeSettingsList.SELECTED_COURSES && (
				<>
					<div
						style={{
							display: "flex",
							marginTop: "15px",
							flexWrap: "wrap",
						}}
					>
						<div
							style={{
								display: "flex",
								flexBasis: "100%",
								fontWeight: "bold",
							}}
						>
							{i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.addCourseIdOrUrl")}
						</div>
						<div
							style={{
								display: "flex",
								flexBasis: "100%",
								gap: "15px",
								margin: "10px 0px",
								alignItems: "center",
								justifyContent: "flex-end",
							}}
						>
							<CoreTextBox
								value={courseToAdd}
								onChange={(e) => setCourseToAdd(e.target.value)}
								type="text"
								placeholder={i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.addCourseIdOrUrl")}
								style={{
									width: "calc(100% - 125px)",
									lineHeight: "15px",
								}}
							/>
							<CoreButton
								onClick={() => onAddCourseClick()}
								style={{
									height: "40px",
									width: "110px",
									fontSize: "15px",
								}}
							>
								{i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.add")}
							</CoreButton>
						</div>
					</div>
					<div
						style={{
							display: "flex",
							marginTop: "20px",
							justifyContent: "flex-end",
						}}
					>
						{coursesToDelete.length > 0 && (
							<>
								<CoreModal
									open={isRemoveCourseModalOpen}
									title={i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.removeCourse")}
									buttons={[
										{
											text: i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.cancel"),
											color: "secondary",
											onClick: () => {
												setIsRemoveCourseModalOpen(false);
											},
										},
										{
											text: i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.remove"),
											color: "cancel",
											onClick: () => {
												const selectedCourses = selectedCourseList.filter(
													(course) => !coursesToDelete.includes(course.courseId),
												);
												setCoursesToDelete([]);
												setSelectedCourseList(selectedCourses);
												setScopeSettingsLoadedData({
													...scopeSettingsLoadedData,
													selectedCourses: selectedCourses,
												});
												setIsRemoveCourseModalOpen(false);

												coreToast.success(
													i18n.t("portal.components.settingsPageTSX.removeCourse", {
														numOfCourse:
															coursesToDelete.length + " course" + (coursesToDelete.length > 1 ? "s have" : " has"),
													}),
												);
											},
										},
									]}
									modalVerticalPosition="top"
									modalMargin={"25px 0px 0px 0px"}
									keepTopBorderRadius={true}
									onClose={() => {
										setIsRemoveCourseModalOpen(false);
									}}
								>
									<p>{i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.removeCourseConfirmMsg")}</p>
								</CoreModal>
								<CoreButton
									onClick={() => {
										setIsRemoveCourseModalOpen(true);
									}}
									style={{
										height: "40px",
										width: "110px",
										fontSize: "15px",
									}}
									color="cancel"
								>
									{i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.remove")}
								</CoreButton>
							</>
						)}
					</div>
					<div style={{ marginTop: "10px" }}>
						<CoreTable
							data={selectedCourseList}
							columns={courseColumns}
							fontSize={15}
							headerFontSize={15}
							paginationSize="small"
							padding={"10px 20px"}
							rowHeight={10}
							hidePaginationOnSinglePage={true}
							noResultsString={i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.noCoursesAdded")}
							initialSortKey="cId"
							initialSortOrder="asc"
							sortRequired
						/>
					</div>
				</>
			)}

			<CoreModal
				open={showManageAccountsModal}
				onClose={() => setShowManageAccountsModal(false)}
				title={i18n.t("subAccount.manage", { type: i18n.t(`subAccount.plural.${props.lmsType}`) })}
				widthFixed={true}
				minWidth="min(60%, 1200px)"
				buttons={
					<div style={{ display: "block", width: "100%" }}>
						<div style={{ float: "right" }}>
							<CoreButton
								style={{ marginRight: "8px" }}
								color="secondary"
								onClick={() => {
									setShowManageAccountsModal(false);
									setIsModalLoading(false);
								}}
							>
								{i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.cancel")}
							</CoreButton>
							<CoreButton onClick={() => onSaveAccountClick()} disabled={isModalLoading}>
								{i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.save")}
							</CoreButton>
						</div>
					</div>
				}
			>
				<div style={{ margin: "0px 5px" }}>
					<CoreSearchFieldPaper>
						<CoreSearchField
							label={i18n.t("subAccount.searchLabel", { type: i18n.t(`subAccount.singular.${props.lmsType}`) })}
							onSearch={(value) => setSearchBarInput(value)}
							ariaControls={controlId}
							totalResults={
								(searchBarInput.length > 0 ? filteredSelectedTree.length : selectedTree.length) +
								(searchBarInput.length > 0 ? filteredUnselectedTree.length : unSelectedTree.length)
							}
							value={searchBarInput}
						/>
					</CoreSearchFieldPaper>

					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							marginTop: "25px",
							gap: "50px",
							justifyContent: "center",
						}}
						id={controlId}
					>
						<div className={styles["account-div"]}>
							<div
								className={styles["account-div-title"]}
							>{`${i18n.t(`subAccount.singular.${props.lmsType}`)} List`}</div>
							<div className={styles["account-div-table"]}>
								{isModalLoading ? (
									<div
										style={{
											display: "flex",
											rowGap: "15px",
											flexDirection: "column",
											marginTop: "10px",
										}}
									>
										<Skeleton variant="rounded" width={"100%"} animation="wave" />
										<Skeleton variant="rounded" width={"100%"} animation="wave" />
										<Skeleton variant="rounded" width={"100%"} animation="wave" />
										<Skeleton variant="rounded" width={"100%"} animation="wave" />
									</div>
								) : (
									<>
										<CoreTreeView
											disableSelection={true}
											items={searchBarInput.length > 0 ? filteredUnselectedTree : unSelectedTree}
											label={CustomAddLabel}
											getItemId={(item: LmsAccount) => `${item.id}`}
											getItemLabel={(item: LmsAccount) => `${item?.accountName} (${item?.lmsAccountId})`}
											noResultString={
												props.lmsType === "canvas"
													? i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.noAccFound")
													: i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.noNodesFound")
											}
											noResultIcon={NoAccountListSvg}
										/>
									</>
								)}
							</div>
						</div>
						<div className={styles["account-div"]}>
							<div className={styles["account-div-title"]}>
								{i18n.t("subAccount.manage", { type: i18n.t(`subAccount.plural.${props.lmsType}`) })}
							</div>
							<div className={styles["account-div-table"]}>
								{isModalLoading ? (
									<div
										style={{
											display: "flex",
											rowGap: "15px",
											flexDirection: "column",
											marginTop: "10px",
										}}
									>
										<Skeleton variant="rounded" width={"100%"} animation="wave" />
										<Skeleton variant="rounded" width={"100%"} animation="wave" />
										<Skeleton variant="rounded" width={"100%"} animation="wave" />
										<Skeleton variant="rounded" width={"100%"} animation="wave" />
									</div>
								) : (
									<>
										<CoreTreeView
											disableSelection={true}
											items={searchBarInput.length > 0 ? filteredSelectedTree : selectedTree}
											label={CustomRemoveLabel}
											getItemId={(item: LmsAccount) => `${item.id}`}
											getItemLabel={(item: LmsAccount) => `${item?.accountName} (${item?.lmsAccountId})`}
											noResultString={
												props.lmsType === "canvas"
													? i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.addedAccsLocationMsg")
													: i18n.t("portal.components.settings.instituteScopeSettings.IndexTSX.addedNodesLocationMsg")
											}
											noResultIcon={NoMangeAccountSvg}
										/>
									</>
								)}
							</div>
						</div>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "center",
							marginTop: "20px",
						}}
					>
						<span>
							{i18n.t("subAccount.notSeeingAll", { type: i18n.t(`subAccount.plural.${props.lmsType}`) })}
							<CoreButton onClick={onSyncAccountClick} disabled={isModalLoading}>
								{i18n.t("subAccount.sync", { type: i18n.t(`subAccount.plural.${props.lmsType}`) })}
							</CoreButton>
						</span>
					</div>
				</div>
			</CoreModal>
		</ThemeWrapper>
	);
};

export async function saveScopeSettings(data?: { scopeSettingsData: ScopeSettingsInfo }) {
	try {
		if (data?.scopeSettingsData) {
			const updateData = {
				scopeMode: data.scopeSettingsData.scopeMode,
				enabledAccounts: data.scopeSettingsData.allAccounts
					.filter((account) => {
						return account.verityEnabled;
					})
					.map((account) => account.id),
				enabledCourses: data.scopeSettingsData.selectedCourses.map((course) => course.courseId),
			};
			await apiClient.institute.saveInstituteScopeSettings(updateData);
		}
		return { ok: true };
	} catch (err) {
		console.error(err);
		return { ok: false, reason: "Failed to save scope setting." };
	}
}

function filterSubAccountTreeBySearchText(subAccountTree: Array<LmsAccount>, searchText: string): Array<LmsAccount> {
	let result: Array<LmsAccount> = [];
	if (!subAccountTree || subAccountTree.length == 0) return [];
	subAccountTree.forEach((treeNode: LmsAccount) => {
		if (
			treeNode.lmsAccountId.toLowerCase().includes(searchText.toLowerCase()) ||
			treeNode.accountName.toLowerCase().includes(searchText.toLowerCase())
		) {
			result.push(treeNode);
		} else {
			result = result.concat(filterSubAccountTreeBySearchText(treeNode.children ?? [], searchText));
		}
	});
	return result;
}
